import React from "react";
import { get, isNil, cloneDeep, chunk } from "lodash";
import moment from "moment";

import { notify, TYPE } from "@8x8/oxygen-toaster";
import { ImageIcon } from "@8x8/oxygen-icon";
import Modal from "@8x8/oxygen-modal";
import Button from "@8x8/oxygen-button";

import PageTitle from "@/react/components/PageTitle";
import {
  useGetEmailTemplatesQuery,
  useSendEmailMutation,
} from "@/react/features/send-email/api";
import ContentWrapper from "@/react/features/styled/PageContentContainer";
import PageLayout from "@/react/features/styled/PageLayout";
import LabeledSelect from "@/react/components/LabeledSelect";
import LabeledDateSelector from "@/react/components/LabeledDateSelector";
import LabeledInput from "@/react/components/LabeledInput";
import LabeledTaggedSelect from "@/react/components/LabeledTaggedSelect";
import { errorNotification, useErrorNotification } from "@/react/notifications";
import { useTable } from "@/react/components/table/useTable";
import { filterTableMutations } from "@/react/components/table/filterTableMutations";
import TableWrapper from "@/react/features/styled/TableWrapper";
import TableComponent from "@/react/components/table/TableComponent";

import columnDefinition from "./columnDefinition";
import schema from "./schema";
import {
  EmailDetailsContainer,
  EmailDetailsSubContainer,
  VersionContainer,
  ThumbnailButtonWrapper,
  ThumbnailButton,
  ThumbnailImageContainer,
  SendButtonWrapper,
} from "./styled";

const UNTITLED = "Untitled";
const DEFAULT_FROM_ADDRESS = "noreply@8x8.com";
const DEFAULT_FROM_NAME = "8x8 Inc";

const TEMPLATE_HTML_ID = "template-select";
const VERSION_HTML_ID = "version-select";

const RECIPIENT_CHUNK_LIMIT = 700;

const formatData = (data, labelMember, idMember, sorter) => {
  let options = data?.map((element) => ({
    label: get(element, labelMember) ?? UNTITLED,
    value: get(element, idMember),
    details: element,
  }));
  if (!isNil(options) && !isNil(sorter)) {
    options.sort(sorter);
  }

  const mapping = data?.reduce((map, element) => {
    map[element.id] = element;
    return map;
  }, {});
  return { options, mapping };
};

const columns = columnDefinition();

const SendEmailPage = () => {
  let today = new Date();
  today.setHours(0, 0, 0, 0);

  const {
    data: templatesData,
    isFetching: isFetchingTemplates,
    error: templatesError,
  } = useGetEmailTemplatesQuery();
  const templates = React.useMemo(
    () => templatesData?.templates,
    [templatesData]
  );

  const [selectedTemplate, setSelectedTemplate] = React.useState(null);
  const [selectedVersion, setSelectedVersion] = React.useState(null);
  const [customSubjectVersion, setCustomSubjectVersion] = React.useState(null);
  const [selectedTags, setSelectedTags] = React.useState([]);
  const [selectedDate, setSelectedDate] = React.useState(null);
  const [fromAddress, setFromAddress] = React.useState(null);
  const [fromName, setFromName] = React.useState(null);
  const [showThumbnail, setShowThumbnail] = React.useState(false);

  const { options: templateOptions, mapping: templateMap } =
    React.useMemo(() => {
      let { options, mapping } = formatData(templates, "name", "id");

      if (options) {
        // If any template has no versions, mark it as inactive
        for (const item of options) {
          const templateVersions = item.details?.versions;
          if (isNil(templateVersions) || templateVersions.length === 0) {
            item.label = "[INACTIVE] " + item.label;
            item.isDisabled = true;
          }
        }
      }

      return { options, mapping };
    }, [templates]);

  // The options are sorted so that the active one is first
  // If there is a custom one, that is placed first
  const { options: versionOptions, mapping: versionsMap } =
    React.useMemo(() => {
      let { options, mapping } = formatData(
        selectedTemplate?.versions,
        "subject",
        "id",
        (version1, version2) =>
          version2.details.active - version1.details.active
      );

      if (options) {
        // Show all version subjects, but only the first one is active, so mark the others as inactive
        for (let index = 1; index < options.length; index++) {
          options[index].label = "[INACTIVE] " + options[index].label;
          options[index].isDisabled = true;
        }

        // If there is a custom subject option, put it at the top
        // Then add the version info to the mapping
        if (customSubjectVersion) {
          const customSubjectId = crypto.randomUUID();
          options = [
            { label: customSubjectVersion.subject, value: customSubjectId },
          ].concat(options);
          mapping[customSubjectId] = customSubjectVersion;
        }
      }

      return { options, mapping };
    }, [selectedTemplate, customSubjectVersion]);

  // Reset version when template is cleared
  React.useEffect(() => {
    if (isNil(selectedTemplate)) {
      setSelectedVersion(null);
      setCustomSubjectVersion(null);
    } else if (versionOptions) {
      setSelectedVersion(versionsMap[versionOptions[0].value]);
    }
  }, [selectedTemplate, versionOptions]);

  useErrorNotification(templatesError);

  const [fields, setFields] = React.useState([]);

  const { table, filters, state, mutations } = useTable(
    fields,
    columns,
    schema
  );

  const finalMutations = React.useMemo(
    () =>
      filterTableMutations(mutations, {
        add: true,
        update: true,
        delete: true,
      }),
    [mutations]
  );
  const {
    rowsToCreate,
    rowsToUpdate,
    rowsToDelete,
    resetData,

    data,
  } = finalMutations;

  const isDirty =
    rowsToCreate?.size > 0 || rowsToUpdate?.size > 0 || rowsToDelete?.size > 0;

  const [sendEmails, { isLoading: isSendingEmails }] = useSendEmailMutation();
  const onSave = React.useCallback(() => {
    setFields(data);
  }, [data]);

  const onSendEmails = React.useCallback(() => {
    let personalizations = [];
    fields.forEach((row) => {
      let pbx = [];
      if (row.pbxName) {
        row.pbxName.split(";").forEach((item) => pbx.push({ pbxName: item }));
      }

      personalizations.push({
        to: [
          {
            email: row.email,
            name: row.firstName + " " + row.lastName,
          },
        ],
        dynamic_template_data: {
          pbx,
          firstName: row.firstName,
          date: moment(selectedDate).format("MMMM Do YYYY"),
          to: row.email,
        },
        subject: selectedVersion.subject,
      });
    });

    const bodySharedData = {
      ...(selectedTags.length > 0 ? { categories: selectedTags } : {}),
      from: {
        email: fromAddress ?? DEFAULT_FROM_ADDRESS,
        name: fromName ?? DEFAULT_FROM_NAME,
      },
      reply_to: {
        email: fromAddress ?? DEFAULT_FROM_ADDRESS,
        name: fromName ?? DEFAULT_FROM_NAME,
      },
      template_id: selectedTemplate.id,
    };

    const personalizationChunks = chunk(
      personalizations,
      RECIPIENT_CHUNK_LIMIT
    );
    Promise.all(
      personalizationChunks.map((recipients) => {
        const body = {
          personalizations: recipients,
          ...bodySharedData,
        };
        return sendEmails(body);
      })
    )
      .then((responses) => {
        const error = responses.find((response) => !isNil(response.error));
        if (error) {
          errorNotification(
            error.error ?? error,
            (error) =>
              get(error, "data.errors") &&
              get(error, "data.errors").reduce(
                (fullErrorMessage, currentError) =>
                  fullErrorMessage.concat(` ${currentError.message}`),
                ""
              )
          );
          return;
        }
        if (
          responses.every((response) => isNil(response) || isNil(response.data))
        ) {
          notify({
            type: TYPE.success,
            content: `Emails Sent`,
            duration: 5000,
          });
        }
        return responses;
      })
      .catch(errorNotification);
  }, [
    selectedTemplate,
    selectedVersion,
    selectedTags,
    selectedDate,
    fromAddress,
    fromName,
    fields,
  ]);

  const onCreateSubject = React.useCallback(
    (newLabel) => {
      // The custom option has the same value as the current version, because we still want to use that version,
      // just with a different subject title
      if (newLabel !== selectedVersion.subject) {
        let versionInfoWithSubject = cloneDeep(selectedVersion);
        versionInfoWithSubject.subject = newLabel;
        setCustomSubjectVersion(versionInfoWithSubject);
      }
    },
    [selectedVersion]
  );

  return (
    <PageLayout>
      {showThumbnail && (
        <Modal
          onClose={() => setShowThumbnail(false)}
          shouldCloseOnOverlayClick
          shouldCloseOnEsc
          size={"big"}
          height={"fit-content"}
          width={"fit-content"}
        >
          <ThumbnailImageContainer>
            <img
              src={selectedVersion.thumbnail_url}
              alt={"Selected email thumbnail"}
            />
          </ThumbnailImageContainer>
        </Modal>
      )}
      <ContentWrapper>
        <PageTitle>Send Email</PageTitle>
        <EmailDetailsContainer>
          <EmailDetailsSubContainer column={"1 / 3"}>
            <LabeledSelect
              prompt={"Template"}
              placeholder={"Choose template"}
              isDisabled={isFetchingTemplates}
              options={templateOptions}
              value={selectedTemplate?.name}
              onSelectionChanged={(selection) =>
                setSelectedTemplate(
                  isNil(selection) ? null : templateMap[selection]
                )
              }
              htmlId={TEMPLATE_HTML_ID}
              isClearable
            />
          </EmailDetailsSubContainer>
          <EmailDetailsSubContainer column={"3 / 5"}>
            <VersionContainer>
              <LabeledSelect
                prompt={"Subject"}
                placeholder={
                  selectedTemplate
                    ? "Enter or select email subject"
                    : "Choose template first"
                }
                isDisabled={isFetchingTemplates || isNil(selectedTemplate)}
                options={versionOptions}
                value={selectedVersion?.subject}
                onSelectionChanged={(selection) =>
                  setSelectedVersion(
                    isNil(selection) ? null : versionsMap[selection]
                  )
                }
                onOptionCreatedRequest={onCreateSubject}
                htmlId={VERSION_HTML_ID}
              />
              {selectedVersion && (
                <ThumbnailButtonWrapper>
                  <ThumbnailButton>
                    <ImageIcon onClick={() => setShowThumbnail(true)} />
                  </ThumbnailButton>
                </ThumbnailButtonWrapper>
              )}
            </VersionContainer>
          </EmailDetailsSubContainer>
          <LabeledTaggedSelect
            prompt={"Category"}
            placeholder={"Add tag..."}
            onSelectionChanged={(tags) =>
              setSelectedTags(tags.map((tag) => tag.value))
            }
          />
          <LabeledDateSelector
            value={selectedDate}
            setValue={setSelectedDate}
            defaultValue={today}
            singleDate
            prompt={"Date"}
            placeholder={"Choose date"} // Placeholder never appears when we use a default datetime
          />
          <LabeledInput
            prompt={"From Address"}
            placeholder={DEFAULT_FROM_ADDRESS}
            value={fromAddress}
            setValue={setFromAddress}
          />
          <LabeledInput
            prompt={"From Name"}
            placeholder={DEFAULT_FROM_NAME}
            value={fromName}
            setValue={setFromName}
          />
        </EmailDetailsContainer>
        <TableWrapper>
          <TableComponent
            title="Email Fields"
            schema={schema}
            isDirty={isDirty}
            isLoading={false}
            onSave={onSave}
            onCancel={resetData}
            table={table}
            filters={filters}
            mutations={finalMutations}
            state={state}
          />
        </TableWrapper>
        <SendButtonWrapper>
          <Button
            style={{ width: "50%" }}
            isDisabled={
              isSendingEmails ||
              isDirty ||
              isNil(selectedVersion) ||
              isNil(selectedTemplate) ||
              fields.length === 0
            }
            onClick={onSendEmails}
          >
            Submit
          </Button>
        </SendButtonWrapper>
      </ContentWrapper>
    </PageLayout>
  );
};

export default SendEmailPage;
